import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import logger from "@/lib/logger";

export const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider",
    );
  }
  return context;
};

/**
 * NotificationProvider - Real-time notification system for pending payments
 *
 * Features:
 * - Polls for new pending payments every 30 seconds
 * - Shows toast notifications for new payments
 * - Tracks pending payment count for badge displays
 * - Dispatches events for quick approval modal
 */
export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const lastCheckedRef = useRef(null);
  const [isPolling, setIsPolling] = useState(false);

  // Fetch pending payments - using ref for lastChecked to avoid dependency issues
  const fetchPendingPayments = useCallback(
    async (showToasts = false) => {
      if (!isAuthenticated) return;

      try {
        // TODO: The /payments/pending endpoint doesn't exist. The backend has
        // /groups/{group_id}/pending-payments which requires a groupId.
        // This notification feature needs to be refactored to work within
        // a group context. For now, we skip polling to avoid 405 errors.
        const payments = []; // Disabled until proper implementation

        // Check for new payments since last check
        if (showToasts && lastCheckedRef.current && payments.length > 0) {
          const newPayments = payments.filter(
            (p) => new Date(p.created_at) > new Date(lastCheckedRef.current),
          );

          // Show toast for each new payment
          newPayments.forEach((payment) => {
            toast.info(
              `New payment from ${payment.member_name || "Unknown"}: $${payment.amount?.toFixed(2) || "0.00"}`,
              {
                action: {
                  label: "Review",
                  onClick: () => {
                    // Dispatch event for quick approval modal
                    window.dispatchEvent(
                      new CustomEvent("open-payment-approval", {
                        detail: { payment },
                      }),
                    );
                  },
                },
                duration: 10000,
              },
            );
          });
        }

        setPendingPayments(payments);
        setPendingCount(payments.length);
        lastCheckedRef.current = new Date().toISOString();
      } catch (error) {
        // Don't show error for 404/405 (endpoint might not exist yet)
        if (error.response?.status !== 404 && error.response?.status !== 405) {
          logger.error("Failed to fetch pending payments:", error);
        }
      }
    },
    [isAuthenticated],
  );

  // Initial fetch and polling
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Initial fetch (without toasts) and setup polling
    const initPolling = async () => {
      await fetchPendingPayments(false);
      setIsPolling(true);
    };
    initPolling();

    // Set up polling interval (30 seconds)
    const interval = setInterval(() => {
      fetchPendingPayments(true); // Show toasts for new payments
    }, 30000);

    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [isAuthenticated, fetchPendingPayments]);

  // Reset state when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Reset state in a timeout to avoid setState during render
      const resetState = () => {
        setPendingPayments((prev) => (prev.length > 0 ? [] : prev));
        setPendingCount((prev) => (prev !== 0 ? 0 : prev));
      };
      setTimeout(resetState, 0);
    }
  }, [isAuthenticated]);

  // Manual refresh function
  const refreshPendingPayments = useCallback(() => {
    return fetchPendingPayments(false);
  }, [fetchPendingPayments]);

  // Remove a payment from the list (after approval/rejection)
  const removePayment = useCallback((paymentId) => {
    setPendingPayments((prev) => prev.filter((p) => p.id !== paymentId));
    setPendingCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setPendingPayments([]);
    setPendingCount(0);
  }, []);

  const value = {
    pendingPayments,
    pendingCount,
    isPolling,
    refreshPendingPayments,
    removePayment,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
